import React, { useState, useCallback, useMemo } from "react";
import usePortDetails, { PortHookInterface } from "./portDetailsHook";
import Node from "./blocks/NodeBlock";
import { useIsMounted } from "../../utils/customHooks";
import useLeftRightContext, { leftRightContextType } from "./leftRightContextHook";
import { isBandwidthHook, useBandwidthHook } from "./useBandwidthHook";
import BandwidthSection from "./blocks/BandwidthSection";
import CrossConnectSection from "./blocks/CrossConnectSection";
import Spinner from "../DesignSystem/spinner";
import { useOpenClose } from "../../utils/hooks/OpenCloseHook";
import { FullScreenDialog } from "./FullScreenDialog";
import EditIcon from "../../assets/images/edit-icon.svg";
import WaveXchangeLogoSmall from "../../assets/images/wavexchange-small-icon.svg";
import { wrapInTooltip } from "../../utils/methods";
import { MenuItem } from "@material-ui/core";
import ShiftButton from "./blocks/ShiftButton";
import WaveXchangeLogo from "../../assets/images/wavexchange-logo-color.svg";
import Button from "../DesignSystem/button";

type button = "add_connect" | "edit_connect" | "edit_connect_mini" | "add_connect_mini";
interface PortDetailsInterface {
  nrId: number;
  otherNrId?: number;
  isBandwidth?: boolean;
  doNotSortLeftRight?: boolean;
  refreshIndex: () => void;
  onCloseContextMenu?: () => void;
  buttonType?: button;
}

const getLeftRight = (left: number, right: number | undefined, doNotSort?: boolean) => {
  if (right && right > left && !doNotSort) return { left: right, right: left };
  return { left, right };
};
export const PortDetails: React.FC<PortDetailsInterface> = (props) => {
  const isMounted = useIsMounted();
  const dialogOpenHook = useOpenClose();
  const [isChanged, setIsChanged] = useState(false);
  React.useEffect(() => {
    if (!isMounted) return;
    if (dialogOpenHook.isOpen) {
      setIsChanged(false);
    } else {
      if (isChanged) props.refreshIndex();
    }
  }, [dialogOpenHook.isOpen]);
  const [ports, setPorts] = useState<{ left: number | undefined; right: number | undefined }>(
    getLeftRight(props.nrId, props.otherNrId, props.doNotSortLeftRight)
  );
  React.useEffect(() => {
    setPorts(getLeftRight(props.nrId, props.otherNrId, props.doNotSortLeftRight));
  }, [props.nrId, props.otherNrId]);
  const leftProps: PortHookInterface = usePortDetails(ports.left, isMounted, dialogOpenHook.isOpen, () => setIsChanged(true), props.refreshIndex);
  const rightProps: PortHookInterface = usePortDetails(ports.right, isMounted, dialogOpenHook.isOpen, () => setIsChanged(true), props.refreshIndex);
  const bandwidthHook = useBandwidthHook(leftProps, rightProps, isMounted, () => setIsChanged(true), props.refreshIndex);

  const { portDetails: leftPortDetails, loading: leftLoading } = leftProps;
  const { portDetails: rightPortDetails, loading: rightLoading } = rightProps;
  const leftRightContext: leftRightContextType = useLeftRightContext(leftProps.portDetails, rightProps.portDetails);
  const shift = useCallback(
    (direction: "left" | "right") => {
      if (direction === "left") {
        setPorts({
          left: leftRightContext.leftPointer,
          right: leftPortDetails?.nr_id,
        });
      } else if (direction === "right") {
        setPorts({
          left: rightPortDetails?.nr_id,
          right: leftRightContext.rightPointer,
        });
      }
    },
    [leftRightContext, leftPortDetails, rightPortDetails]
  );

  const isBandwidthPage = useMemo(() => isBandwidthHook(bandwidthHook), [bandwidthHook, leftProps]);
  const middleSection = useMemo(() => {
    if (isBandwidthHook(bandwidthHook)) return <BandwidthSection portProps={leftProps} otherPortProps={rightProps} bandwidthProps={bandwidthHook} />;
    else if (leftPortDetails && rightPortDetails) return <CrossConnectSection port={leftPortDetails} />;
    return <div />;
  }, [bandwidthHook, leftPortDetails, rightPortDetails]);

  let content;

  if (leftLoading || rightLoading || leftProps.groupService.GroupDropdownProps.loading || rightProps.groupService.GroupDropdownProps.loading)
    content = (
      <div className="index-port-view flex">
        <Spinner size={50} />
      </div>
    );
  else
    content = (
      <>
        <div className="index-port-view">
          <div className="flex">
            {leftRightContext.leftPointer && <ShiftButton direction="left" onClick={shift} context={leftRightContext} />}
            {leftPortDetails && <Node portProps={leftProps} showTitle={leftRightContext.type !== "cross-connect"} isBandwidthPage={isBandwidthPage} />}
            {middleSection && middleSection}
            {rightPortDetails && <Node portProps={rightProps} showTitle={leftRightContext.type !== "cross-connect"} isBandwidthPage={isBandwidthPage} />}
            {leftRightContext.rightPointer && <ShiftButton direction="right" onClick={shift} context={leftRightContext} />}
          </div>
        </div>
        {leftProps.groupService.GroupDrawerProps.drawerComponent}
        {rightProps.groupService.GroupDrawerProps.drawerComponent}
      </>
    );
  const tooltipTitle = props.isBandwidth ? "See Bandwidth Details" : "See Port Details";
  const buttons = {
    default: wrapInTooltip(<img src={EditIcon} className="pointer" alt="See port details" onClick={() => dialogOpenHook.open()} />, tooltipTitle, "right"),
    add_connect: wrapInTooltip(
      <div className="move-to-wx-button">
        <Button
          onClick={() => {
            dialogOpenHook.open();
            leftProps.manageLeasedPorts.openDrawer();
          }}
          variant="green"
          node={
            <div className="flex">
              Connect to
              <img src={WaveXchangeLogoSmall} alt="connect to wavexchange" />
            </div>
          }
        />
      </div>,
      "Add Connection To Switch",
      "right"
    ),
    edit_connect: wrapInTooltip(
      <div style={{ height: "32px", display: "flex", justifyContent: "center" }}>
        <img src={WaveXchangeLogo} alt="WaveXchange logo" style={{ height: "32px", display: "flex", justifyContent: "center" }} />
        <img
          src={EditIcon}
          alt="edit"
          className="pointer"
          onClick={() => {
            dialogOpenHook.open();
            leftProps.manageLeasedPorts.openDrawer();
          }}
        />
      </div>,

      "Manage Connection To Switch",
      "right"
    ),
    add_connect_mini: wrapInTooltip(
      <div className="move-to-wx-button">
        <Button
          onClick={() => {
            dialogOpenHook.open();
            leftProps.manageLeasedPorts.openDrawer();
          }}
          variant="green"
          node={
            <div className="flex">
              <img src={WaveXchangeLogoSmall} alt="connect to wavexchange" />
            </div>
          }
        />
      </div>,
      "Add Connection To Switch",
      "right"
    ),
    edit_connect_mini: wrapInTooltip(
      <div style={{ height: "32px", display: "flex", justifyContent: "center" }}>
        <img
          src={WaveXchangeLogo}
          onClick={() => {
            dialogOpenHook.open();
            leftProps.manageLeasedPorts.openDrawer();
          }}
          alt="WaveXchange logo"
          style={{ height: "32px", display: "flex", justifyContent: "center", cursor: "pointer" }}
        />
      </div>,
      "Manage Connection To Switch",
      "right"
    ),
  };
  let button = buttons.default;
  if (props.buttonType) button = buttons[props.buttonType];

  const tooltip = `
  left: ${leftPortDetails?.nr_id || ""}
  -
  right: ${rightPortDetails?.nr_id || ""}
  `;
  return (
    <>
      {props.onCloseContextMenu ? <MenuItem onClick={() => dialogOpenHook.open()}>Resource Details</MenuItem> : button}
      <FullScreenDialog title={wrapInTooltip(<span>Resource Details</span>, tooltip)} content={content} openHook={dialogOpenHook} />
    </>
  );
};
